import { useUpdateEffect } from 'ahooks';
import random from 'lodash/random';
import React, { FC, useState } from 'react';
import { usePageVisibility } from 'react-page-visibility';
import { DocumentsAPI } from 'api/document/document-core';
import { DocumentId, ProjectId } from 'api/models';
import { useExponentReRender } from 'hooks/use-exponent-rerender';
import { logger } from 'services';
import { ConnectedCKCollabEditor } from './ck-collab-editor';
import { CkeBaseProps } from './custom-editor.types';
import { checkforFatalCKECollaborationPluginError } from './helpers';

// Any random number can be assigned as the key value for the editor. Since the frequency of
// changing the key is low, this is safe.
const RANDOM_ID_MAX_LIMIT = 10000000000;

export const CKCollabEditor: FC<CkeBaseProps> = (props) => {
  const { docId, projectId, singleUserMode } = props;
  const isTabVisible = usePageVisibility();
  const [ckEditorInstanceId, setEditorInstanceId] = useState<number>(docId);
  const [shouldMount, exponentReMount, exponentRetryTimes, isExceed] =
    useExponentReRender(5);

  const refreshComponent = () => {
    setEditorInstanceId(random(RANDOM_ID_MAX_LIMIT));
  };

  // useUpdateEffect omits the first render and only runs when dependencies update
  useUpdateEffect(() => {
    const mayRemountInstance = async () => {
      if (projectId && docId) {
        const sessionStatus = await DocumentsAPI.getCollaborationSession(
          ProjectId(projectId),
          DocumentId(docId),
        );

        if (!sessionStatus?.publicDocSession?.exists) {
          logger.info(
            `CKEditor: Public Session not found. Reloading the editor instance : ${JSON.stringify(
              sessionStatus,
            )}`,
            logger.warn(
              `public session not found: ${JSON.stringify(sessionStatus)}`,
            ),
          );
          refreshComponent();
        }
      }
    };

    // Missing edge cases: (ie, when isTabVisible is not getting updates)
    // 1. When User switches between different programs like Chrome and a video player.
    // 2. When user switches in between 2 tabs in same space.
    if (isTabVisible && !singleUserMode) {
      mayRemountInstance();
    }
  }, [projectId, docId, isTabVisible, singleUserMode]);

  const reMountEditor = (error: Error) => {
    logger.warn(
      `trying to remount cke editor at spaces. retry times ${exponentRetryTimes}`,
    );
    if (isExceed) {
      logger.error('max retries exceed to mount collab editor', { error });

      return;
    }
    exponentReMount();
  };

  const onEditorError = (error: Error) => {
    logger.error(`error in cke ${exponentRetryTimes}`, { error });
    checkforFatalCKECollaborationPluginError(
      error?.message ?? '',
      refreshComponent,
    );
    reMountEditor(error);
  };

  // eslint-disable-next-line react/jsx-props-no-spreading
  return (
    <>
      {shouldMount ? (
        <ConnectedCKCollabEditor
          {...props}
          key={ckEditorInstanceId}
          onEditorError={onEditorError}
          onErrorRetry={reMountEditor}
        />
      ) : null}
    </>
  );
};
